import { useEffect, useMemo, useRef, useState } from "react";
import TrashIcon from "@heroicons/react/24/outline/TrashIcon";
import { useMutation } from "@tanstack/react-query";

import cx from "@hsl/core/utils/cx";
import { createFundWatchData } from "@hsl/fc-subscription/lib";
import useFCSubscriptionStore from "@hsl/fc-subscription/store";

import useFundData from "../../../hooks/useFundData";
import { Translate } from "../../../hooks/useTranslation";
import {
    SubscribableAssetsService,
    type SubscribableAsset,
    type SubscribableAssetUpdate,
} from "../../../openapi";
import useExplorerStore from "../../../store/store";
import Button from "../../Button";
import { TextSkeleton } from "../../Skeletons";
import { useDocumentStructure } from "../SubscriptionFlows/Steps/SelectDocuments/context";

interface Props {
    type: string;
    fundId: string;
    subscriptions: Required<SubscribableAsset>[];
    showSelectedDocuments?: boolean;
}

export const AcceptFundRow = ({
    type,
    fundId,
    subscriptions,
    showSelectedDocuments,
}: Props) => {
    const locale = useExplorerStore((state) => state.config.locale);
    const userId = useFCSubscriptionStore((state) => state.user?.userId);
    const [showAll, setShowAll] = useState(false);
    const ref = useRef<HTMLParagraphElement>(null);
    const userSubscriptions = useFCSubscriptionStore(
        (state) => state.userSubscriptions,
    );
    const fund = useFundData(`fund-${fundId}`);
    const refreshSubscriptions = useFCSubscriptionStore(
        (state) => state.refreshUserSubscriptions,
    );

    const query = useDocumentStructure(fund?.data.share_classes);

    const { mutate: deleteSubscriptions, isLoading: isDeleting } = useMutation({
        mutationFn: async (subs: string[]) => {
            if (!userId) throw new Error("No user");
            await SubscribableAssetsService.deleteSubscriptions(userId, subs);
            await refreshSubscriptions();
        },
    });

    const { mutate: acceptSubscriptions, isLoading: isAccepting } = useMutation(
        {
            mutationFn: async (subs: string[]) => {
                if (!userId) throw new Error("No user");
                if (
                    !userSubscriptions.some(
                        (subs) =>
                            subs.fundId === `${type}-${fundId}` &&
                            subs.subscriptionType === "WATCHLIST",
                    )
                ) {
                    if (!fund) throw new Error("No fund");
                    await SubscribableAssetsService.createSubscriptions(
                        userId,
                        [createFundWatchData(fund)],
                    );
                }

                await SubscribableAssetsService.updateSubscriptions(
                    userId,
                    subscriptions
                        .filter((sub) => subs.includes(sub.subscriptionId))
                        .map(
                            (sub) =>
                                ({
                                    subscriptionId: sub.subscriptionId,
                                    subscriptionStatus: "CONFIRMED",
                                    frequency: sub.frequency,
                                    subscriptionType: sub.subscriptionType,
                                }) satisfies SubscribableAssetUpdate,
                        ),
                );
                await refreshSubscriptions();
            },
        },
    );

    const isShowingDocList = showSelectedDocuments && !query.isLoading;

    useEffect(() => {
        if (!showAll && isShowingDocList && ref.current) {
            setShowAll(ref.current.offsetHeight >= ref.current.scrollHeight);
        }
    }, [isShowingDocList, showAll]);

    const formatter = useMemo(
        () =>
            new Intl.DateTimeFormat([locale.replace("_", "-"), "en-GB"], {
                year: "numeric",
                month: "short",
                day: "numeric",
            }),
        [locale],
    );

    const mostRecentlySharedSubscription = useMemo(() => {
        const dates = subscriptions.sort(
            (a, b) =>
                new Date(b.sharedDateTime).getTime() -
                new Date(a.sharedDateTime).getTime(),
        );

        return dates[0]!;
    }, [subscriptions]);

    const mostRecentlySharedSubscriptionDate = new Date(
        mostRecentlySharedSubscription.sharedDateTime,
    );

    if (!fund) {
        console.error(`Fund ${fundId} not found`);
        return null;
    }

    const documentNames = Array.from(
        new Set(
            subscriptions
                .map(
                    (sub) =>
                        query.data
                            ?.find((x) =>
                                x.subscription_data.subsMap?.some(
                                    (y) => y[2] === sub.subscriptionId,
                                ),
                            )
                            ?.display_name.replace(/\([^)]*\)/g, "")
                            .trim() ?? "",
                )
                .filter(Boolean),
        ),
    );

    return (
        <tr
            className={cx(
                "border-platinum border-b transition-opacity max-md:mt-4 max-md:grid max-md:border [&>td]:px-5 [&>td]:py-2.5",
                (isDeleting || isAccepting) && "pointer-events-none opacity-50",
            )}
        >
            <td className="col-span-3 w-full md:align-baseline">
                <h3 className="text-dark-blue text-lg font-medium">
                    {fund.data.data.name}
                </h3>
            </td>
            <td className="col-span-3">
                <div className="w-max">
                    <p className="text-taupe-grey mb-1 text-sm font-medium md:hidden">
                        <Translate>Shared by</Translate>
                    </p>
                    <p>
                        {mostRecentlySharedSubscription.sharerName}{" "}
                        {mostRecentlySharedSubscription.sharerSurname}
                    </p>
                    <time
                        className="text-taupe-grey text-sm"
                        dateTime={mostRecentlySharedSubscriptionDate.toISOString()}
                    >
                        {formatter.format(mostRecentlySharedSubscriptionDate)}
                    </time>
                </div>
            </td>
            <td
                className={cx(
                    "col-span-3",
                    !showSelectedDocuments && "max-md:hidden",
                )}
            >
                {showSelectedDocuments ? (
                    query.isLoading ? (
                        <div className="w-48">
                            <p className="text-taupe-grey mb-1 text-sm font-medium md:hidden">
                                <Translate>Selected documents</Translate>
                            </p>
                            <TextSkeleton width={16} />
                            <TextSkeleton width={"Show all".length} />
                        </div>
                    ) : (
                        <div className="w-48">
                            <p className="text-taupe-grey mb-1 text-sm font-medium md:hidden">
                                <Translate>Selected documents</Translate>
                            </p>
                            <p
                                ref={ref}
                                className={cx(!showAll && "line-clamp-1")}
                            >
                                {documentNames.join(", ")}
                            </p>
                            {!showAll && (
                                <button
                                    className="underline"
                                    onClick={() => setShowAll(true)}
                                >
                                    <Translate>Show all</Translate>
                                </button>
                            )}
                        </div>
                    )
                ) : (
                    <div className="w-48" />
                )}
            </td>
            <td className="col-span-3 items-center justify-evenly gap-4 text-right max-md:flex max-md:w-full max-md:!py-1">
                <button
                    className="px-3 py-4 underline md:hidden"
                    aria-description="Reject pending subscription"
                    onClick={() =>
                        deleteSubscriptions(
                            subscriptions.map((sub) => sub.subscriptionId),
                        )
                    }
                >
                    <Translate>Delete</Translate>
                </button>
                <Button
                    className="font-medium"
                    color="white"
                    disabled={isAccepting}
                    onClick={() =>
                        acceptSubscriptions(
                            subscriptions.map((sub) => sub.subscriptionId),
                        )
                    }
                >
                    <Translate>Accept</Translate>
                </Button>
            </td>
            <td className="max-md:hidden">
                <button
                    className="mt-1.5"
                    aria-description="Reject pending subscription"
                    onClick={() =>
                        deleteSubscriptions(
                            subscriptions.map((sub) => sub.subscriptionId),
                        )
                    }
                >
                    <TrashIcon className="h-5 w-5" />
                </button>
            </td>
        </tr>
    );
};
